import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ChevronUp, ChevronDown, X, Link2, Unlink, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PreviewPlanModal from "@/components/PreviewPlanModal";
import PlanDaysEditor from "@/components/PlanDaysEditor";

// Safe UUID for older mobile browsers where crypto.randomUUID is undefined.
function safeUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID(); } catch (_) {}
  }
  return "id-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/**
 * Reusable items editor for template/plan items.
 * items: array of {id, exercise_id, sets, reps, rest_seconds, notes, order}
 */
export function PlanItemsEditor({ items, setItems, exercises }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  const addExercise = (ex) => {
    const next = [...items, {
      id: safeUUID(), exercise_id: ex.id,
      sets: 3, reps: "10", rest_seconds: 60, notes: "",
      order: items.length, group_id: null, group_type: null,
    }];
    setItems(next);
    setPickerOpen(false); setPickerSearch("");
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i })));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next.map((it, i) => ({ ...it, order: i })));
  };
  const patch = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));

  // Group with next item: shares group_id + group_type
  const linkNext = (idx, type) => {
    if (idx + 1 >= items.length) return;
    const cur = items[idx];
    const nxt = items[idx + 1];
    // If both already in the same group with same type → unlink instead
    if (cur.group_id && cur.group_id === nxt.group_id && cur.group_type === type) {
      // unlink the next item from the group
      setItems(items.map((it, i) => i === idx + 1 ? { ...it, group_id: null, group_type: null } : it));
      return;
    }
    const gid = cur.group_id && cur.group_type === type ? cur.group_id : safeUUID();
    setItems(items.map((it, i) => {
      if (i === idx || i === idx + 1) return { ...it, group_id: gid, group_type: type };
      return it;
    }));
  };
  const clearGroup = (idx) => patch(idx, { group_id: null, group_type: null });

  const exName = (id) => exercises.find(e => e.id === id)?.name || "—";
  const exMuscle = (id) => exercises.find(e => e.id === id)?.muscle_group || "";

  const filteredEx = exercises.filter(e => {
    const s = pickerSearch.trim().toLowerCase();
    if (!s) return true;
    return e.name.toLowerCase().includes(s) || (e.muscle_group || "").toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="label-caps">Esercizi ({items.length})</div>
        <button type="button" data-testid="add-item-btn" onClick={() => setPickerOpen(true)} className="btn-ghost !py-1.5 !text-[11px] flex items-center gap-2">
          <Plus size={12} /> Aggiungi esercizio
        </button>
      </div>
      <div className="space-y-2">
        {(() => {
          const _dummy = {};
          const GROUP_COLORS_SS = [
            { border: "#3B82F6", bg: "rgba(59,130,246,0.10)", label: "A" },  // Superset base = BLU
            { border: "#8B5CF6", bg: "rgba(139,92,246,0.10)", label: "B" },
            { border: "#06B6D4", bg: "rgba(6,182,212,0.10)", label: "C" },
            { border: "#0EA5E9", bg: "rgba(14,165,233,0.10)", label: "D" },
          ];
          const GROUP_COLORS_CC = [
            { border: "#F97316", bg: "rgba(249,115,22,0.12)", label: "A" },  // Circuito base = ARANCIONE
            { border: "#EC4899", bg: "rgba(236,72,153,0.10)", label: "B" },
            { border: "#EAB308", bg: "rgba(234,179,8,0.10)", label: "C" },
            { border: "#EF4444", bg: "rgba(239,68,68,0.10)", label: "D" },
          ];
          const ssIdx = {}, ccIdx = {};
          items.forEach(it => {
            if (it.group_id && it.group_type === "superset" && !(it.group_id in ssIdx)) ssIdx[it.group_id] = Object.keys(ssIdx).length;
            if (it.group_id && it.group_type === "circuit" && !(it.group_id in ccIdx)) ccIdx[it.group_id] = Object.keys(ccIdx).length;
          });
          return items.map((it, i) => {
          const nextIt = items[i + 1];
          const groupWithNext = nextIt && it.group_id && it.group_id === nextIt.group_id;
          const groupLabel = it.group_type === "superset" ? "Super-set" : it.group_type === "circuit" ? "Circuito" : null;
          const inGroup = !!it.group_id;
          const palette = it.group_type === "circuit" ? GROUP_COLORS_CC : GROUP_COLORS_SS;
          const idxMap = it.group_type === "circuit" ? ccIdx : ssIdx;
          const colorIdx = inGroup ? (idxMap[it.group_id] % palette.length) : -1;
          const color = colorIdx >= 0 ? palette[colorIdx] : null;
          const boxStyle = color ? { borderColor: color.border, background: color.bg, borderWidth: 2 } : {};
          return (
          <div key={it.id || i}
            style={boxStyle}
            className={`border p-3 grid gap-2 grid-cols-[20px_1fr] sm:grid-cols-[24px_1fr_auto] items-start min-w-0 ${inGroup ? "" : "border-white/10"}`}>
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-0.5 text-white/40 hover:text-white"><ChevronUp size={14} /></button>
              <div className="text-[10px] text-white/40 text-center">{i + 1}</div>
              <button type="button" onClick={() => move(i, +1)} className="p-0.5 text-white/40 hover:text-white"><ChevronDown size={14} /></button>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-heading font-bold text-sm uppercase tracking-tight">{exName(it.exercise_id)}</div>
                {groupLabel && color && (
                  <span style={{ background: color.border, color: "#fff" }} className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">
                    {groupLabel} {color.label}
                  </span>
                )}
              </div>
              {exMuscle(it.exercise_id) && <div className="text-[10px] uppercase tracking-widest text-[#A855F7]">{exMuscle(it.exercise_id)}</div>}
              <div className="grid grid-cols-3 gap-2 mt-2">
                <SmallField label="Serie"><input type="number" min="1" value={it.sets} onChange={e => patch(i, { sets: Number(e.target.value) })} className="fld3" /></SmallField>
                <SmallField label="Ripetizioni"><input value={it.reps} onChange={e => patch(i, { reps: e.target.value })} placeholder="10 o 8-12" className="fld3" /></SmallField>
                <SmallField label="Rec. (sec)"><input type="number" min="0" value={it.rest_seconds} onChange={e => patch(i, { rest_seconds: Number(e.target.value) })} className="fld3" /></SmallField>
              </div>
              <SmallField label="Note trainer">
                <input value={it.notes || ""} onChange={e => patch(i, { notes: e.target.value })} placeholder="Es. tempo 3-1-1, ROM completo..." className="fld3" />
              </SmallField>
              {/* Group actions */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {i < items.length - 1 && (
                  <>
                    <button type="button" onClick={() => linkNext(i, "superset")}
                      data-testid={`link-ss-${i}`}
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors flex items-center gap-1 ${groupWithNext && it.group_type === "superset" ? "border-[#A855F7] bg-[#A855F7] text-white" : "border-white/15 hover:border-[#A855F7] text-white/70"}`}>
                      <Link2 size={10} /> {groupWithNext && it.group_type === "superset" ? "Super-set attivo" : "Super-set con prossimo"}
                    </button>
                    <button type="button" onClick={() => linkNext(i, "circuit")}
                      data-testid={`link-cc-${i}`}
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors flex items-center gap-1 ${groupWithNext && it.group_type === "circuit" ? "border-[#A855F7] bg-[#A855F7] text-white" : "border-white/15 hover:border-[#A855F7] text-white/70"}`}>
                      <Link2 size={10} /> {groupWithNext && it.group_type === "circuit" ? "Circuito attivo" : "Circuito con prossimo"}
                    </button>
                  </>
                )}
                {inGroup && (
                  <button type="button" onClick={() => clearGroup(i)} className="text-[10px] uppercase tracking-widest px-2 py-1 text-white/40 hover:text-[#FF3366] flex items-center gap-1">
                    <Unlink size={10} /> Rimuovi dal gruppo
                  </button>
                )}
              </div>
            </div>
            <button type="button" onClick={() => removeItem(i)} className="text-[#FF3366]/70 hover:text-[#FF3366] p-1">
              <Trash2 size={14} />
            </button>
          </div>
        );});
        })()}
        {items.length === 0 && <div className="border border-white/5 p-6 text-center text-white/40 text-sm">Nessun esercizio. Aggiungine dal picker.</div>}
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Scegli esercizio</DialogTitle></DialogHeader>
          <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Cerca…" className="w-full bg-transparent border border-white/15 p-2 focus:border-[#A855F7] outline-none mb-3" />
          <div className="max-h-[400px] overflow-auto space-y-1">
            {filteredEx.map(ex => (
              <button key={ex.id} type="button" onClick={() => addExercise(ex)} className="w-full text-left p-3 border border-white/5 hover:border-[#A855F7] transition-colors">
                <div className="font-heading font-bold text-sm">{ex.name}</div>
                {ex.muscle_group && <div className="text-[10px] uppercase tracking-widest text-[#A855F7]">{ex.muscle_group}</div>}
              </button>
            ))}
            {filteredEx.length === 0 && <div className="text-white/40 text-sm p-4">Nessun esercizio in libreria — creane uno prima.</div>}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .fld3 { width: 100%; background: transparent; border: 1px solid rgba(255,255,255,0.12); padding: 6px 8px; color: #fff; outline: none; font-family: 'Manrope',sans-serif; font-size: 0.85rem; }
        .fld3:focus { border-color: #A855F7; }
      `}</style>
    </div>
  );
}

function SmallField({ label, children }) {
  return <label className="block"><span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">{label}</span>{children}</label>;
}

// ---------------- Templates Tab ----------------
export default function TemplatesTab({ exercises }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [days, setDays] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPlan, setPreviewPlan] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/templates"); setTemplates(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "" }); setDays([]); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, description: t.description || "" }); setDays(t.days || []); setOpen(true); };
  const openPreview = (t) => {
    // Templates list response already contains full items (verified by existing count display).
    setPreviewPlan(t);
    setPreviewOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, description: form.description, days };
      if (editing) await api.patch(`/templates/${editing.id}`, payload);
      else await api.post("/templates", payload);
      toast.success("Template salvato");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare template "${t.name}"?`)) return;
    await api.delete(`/templates/${t.id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-caps">Schede predefinite</div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mt-1">{templates.length} template</h2>
        </div>
        <button data-testid="add-template-btn" onClick={openCreate} className="btn-primary flex items-center gap-2"><Plus size={16} /> Nuovo template</button>
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {templates.map(t => (
          <div key={t.id} data-testid={`template-${t.id}`} className="card-surface p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-heading font-bold uppercase tracking-tight text-lg">{t.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-[#A855F7] mt-0.5">
                  {(t.days || []).length} giorn{(t.days || []).length === 1 ? "o" : "i"} · {(t.items || []).length} esercizi
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`preview-template-${t.id}`} onClick={() => openPreview(t)} className="p-1.5 text-white/60 hover:text-[#A855F7]" title="Anteprima"><Eye size={14} /></button>
                <button data-testid={`edit-template-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 text-white/60 hover:text-white" title="Modifica"><Edit3 size={14} /></button>
                <button data-testid={`del-template-${t.id}`} onClick={() => remove(t)} className="p-1.5 text-[#FF3366]/70 hover:text-[#FF3366]" title="Elimina"><Trash2 size={14} /></button>
              </div>
            </div>
            {t.description && <p className="text-sm text-white/60 mt-2 line-clamp-2">{t.description}</p>}
          </div>
        ))}
        {templates.length === 0 && <div className="card-surface p-8 text-center text-white/40 col-span-full">Nessun template.</div>}
      </div>

      <PreviewPlanModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        plan={previewPlan}
        exercises={exercises}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editing ? "Modifica" : "Nuovo"} template</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-4">
            <label><span className="label-caps block mb-2">Nome scheda</span>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="fld2" data-testid="tpl-name" />
            </label>
            <label><span className="label-caps block mb-2">Descrizione</span>
              <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="fld2" data-testid="tpl-desc" />
            </label>
            <PlanDaysEditor days={days} setDays={setDays} exercises={exercises} />
            <button type="submit" data-testid="tpl-submit" className="btn-primary mt-2">Salva template</button>
          </form>
          <style>{`.fld2{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.15);padding:10px 12px;color:#fff;outline:none;font-family:'Manrope',sans-serif;font-size:0.95rem}.fld2:focus{border-color:#A855F7}`}</style>
        </DialogContent>
      </Dialog>
    </div>
  );
}
